import { ReviewList } from './ReviewList';
import type { AttemptDetail, AttemptListEntry } from './types';

type AttemptReviewsState = Record<string, AttemptDetail | 'loading' | 'error'>;

type RecentResultsPanelProps = {
  results: AttemptListEntry[];
  resultsLoading: boolean;
  resultsTotal: number;
  expandedAttemptIds: Set<string>;
  onToggleAttempt: (attemptId: string) => void;
  attemptReviews: AttemptReviewsState;
  topicLabelMap: Record<string, string>;
  pageStart: number;
  pageEnd: number;
  resultsPage: number;
  totalPages: number;
  onPreviousPage: () => void;
  onNextPage: () => void;
  disablePrevious: boolean;
  disableNext: boolean;
};

export function RecentResultsPanel({
  results,
  resultsLoading,
  resultsTotal,
  expandedAttemptIds,
  onToggleAttempt,
  attemptReviews,
  topicLabelMap,
  pageStart,
  pageEnd,
  resultsPage,
  totalPages,
  onPreviousPage,
  onNextPage,
  disablePrevious,
  disableNext,
}: RecentResultsPanelProps) {
  return (
    <div className="card card-secondary card-outline">
      <div className="card-header border-0 d-flex align-items-center justify-content-between">
        <h3 className="card-title mb-0">
          <i className="fas fa-history me-2" aria-hidden="true" /> Recent Results
        </h3>
        {resultsTotal > 0 && (
          <span className="badge bg-secondary">{resultsTotal} attempts</span>
        )}
      </div>
      <div className="card-body">
        {resultsLoading && <div className="alert alert-info">Loading results…</div>}
        {!resultsLoading && resultsTotal === 0 && (
          <div className="alert alert-light border">No attempts yet. Generate a test to get started.</div>
        )}
        {!resultsLoading && resultsTotal > 0 && results.length === 0 && (
          <div className="alert alert-warning">No attempts on this page.</div>
        )}

        <div className="accordion" id="recentResultsAccordion">
          {results.map((attempt, index) => {
            const isExpanded = expandedAttemptIds.has(attempt.id);
            const reviewState = attemptReviews[attempt.id];
            const headingId = `attempt-heading-${attempt.id}`;
            const collapseId = `attempt-collapse-${attempt.id}`;
            const attemptNumber = pageStart + index;
            return (
              <div key={attempt.id} className="accordion-item">
                <h2 className="accordion-header" id={headingId}>
                  <button
                    className={`accordion-button ${isExpanded ? '' : 'collapsed'}`}
                    type="button"
                    aria-expanded={isExpanded}
                    aria-controls={collapseId}
                    onClick={() => onToggleAttempt(attempt.id)}
                  >
                    <div className="d-flex flex-column flex-md-row w-100 justify-content-between align-items-md-center">
                      <div>
                        <span className="fw-semibold">{new Date(attempt.created_at).toLocaleString()}</span>
                        <div className="small text-muted mt-1">
                          Score: {attempt.score_percent}% • Difficulty:{' '}
                          {typeof attempt.requested_difficulty === 'number'
                            ? `${attempt.requested_difficulty}/10`
                            : '—'}
                          {typeof attempt.requested_count === 'number' ? ` • Questions: ${attempt.requested_count}` : ''}
                        </div>
                      </div>
                      <span className="badge bg-primary">Attempt #{attemptNumber}</span>
                    </div>
                  </button>
                </h2>
                <div
                  id={collapseId}
                  className={`accordion-collapse collapse ${isExpanded ? 'show' : ''}`}
                  aria-labelledby={headingId}
                  data-bs-parent="#recentResultsAccordion"
                >
                  <div className="accordion-body bg-light">
                    <div className="row g-3 mb-3">
                      {attempt.student && (
                        <div className="col-12 col-md-4">
                          <div className="info-box bg-white shadow-sm">
                            <span className="info-box-icon bg-primary text-white">
                              <i className="fas fa-user" aria-hidden="true" />
                            </span>
                            <div className="info-box-content">
                              <span className="info-box-text text-muted">Student</span>
                              <span className="info-box-number">{attempt.student}</span>
                            </div>
                          </div>
                        </div>
                      )}
                      <div className="col-12 col-md-4">
                        <div className="info-box bg-white shadow-sm">
                          <span className="info-box-icon bg-success text-white">
                            <i className="fas fa-percentage" aria-hidden="true" />
                          </span>
                          <div className="info-box-content">
                            <span className="info-box-text text-muted">Score</span>
                            <span className="info-box-number">{attempt.score_percent}%</span>
                          </div>
                        </div>
                      </div>
                      {attempt.topic_hint && (
                        <div className="col-12 col-md-4">
                          <div className="info-box bg-white shadow-sm">
                            <span className="info-box-icon bg-warning text-white">
                              <i className="fas fa-lightbulb" aria-hidden="true" />
                            </span>
                            <div className="info-box-content">
                              <span className="info-box-text text-muted">Hint</span>
                              <span className="info-box-number text-wrap">{attempt.topic_hint}</span>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                    {Array.isArray(attempt.requested_topics) && attempt.requested_topics.length > 0 && (
                      <div className="mb-3">
                        <h6 className="fw-semibold">Topics</h6>
                        <div className="d-flex flex-wrap gap-2">
                          {attempt.requested_topics.map((id) => (
                            <span key={id} className="badge bg-secondary">
                              {topicLabelMap[id] || id}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    {reviewState === 'loading' && <div className="alert alert-info">Loading review…</div>}
                    {reviewState === 'error' && (
                      <div className="alert alert-danger">Review unavailable. Please try again later.</div>
                    )}
                    {reviewState && reviewState !== 'loading' && reviewState !== 'error' && (
                      <ReviewList entries={reviewState.review} />
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      {resultsTotal > 0 && (
        <div className="card-footer bg-white d-flex flex-column flex-md-row justify-content-between align-items-center gap-3">
          <div className="text-muted small">
            Showing {pageStart}-{pageEnd} of {resultsTotal}
          </div>
          <div className="btn-group" role="group" aria-label="Results pagination">
            <button type="button" className="btn btn-outline-secondary" onClick={onPreviousPage} disabled={disablePrevious}>
              Previous
            </button>
            <span className="btn btn-outline-light text-muted">
              Page {resultsPage} of {totalPages}
            </span>
            <button type="button" className="btn btn-outline-secondary" onClick={onNextPage} disabled={disableNext}>
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
